import { useState, type FormEvent } from 'react';
import { signInWithPassword, signUp } from '../lib/supabase/auth';

interface Props {
  open: boolean;
  onClose?: () => void;
}

type Mode = 'signin' | 'signup';
type Status = 'idle' | 'loading' | 'success' | 'error';

export function LoginModal({ open, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg(null);

    try {
      const { error } =
        mode === 'signin'
          ? await signInWithPassword(email, password)
          : await signUp(email, password);

      if (error) {
        setStatus('error');
        setErrorMsg(error.message);
      } else {
        setStatus('success');
        if (mode === 'signup') {
          // After sign-up, switch to sign-in so the user can log in
          setMode('signin');
          setPassword('');
        }
      }
    } catch (err) {
      console.error('[auth] auth threw:', err);
      setStatus('error');
      setErrorMsg(
        err instanceof Error
          ? `Could not reach the sign-in service: ${err.message}`
          : 'Unexpected error. Please try again.',
      );
    }
  }

  const isLoading = status === 'loading';
  const canSubmit = email && password && !isLoading;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-slate-700/60 bg-surface-elevated p-8 shadow-2xl">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-700/50 hover:text-white"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className="mb-6">
          <h2 id="login-title" className="text-2xl font-semibold text-white">
            {mode === 'signin' ? 'Sign in to Cartografika' : 'Create an account'}
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            {mode === 'signin'
              ? 'Enter your email and password to continue.'
              : 'Sign up to get started with Cartografika.'}
          </p>
        </div>

        {status === 'success' && mode === 'signin' ? (
          <div className="rounded-lg border border-emerald-700/60 bg-emerald-900/30 p-4 text-sm text-emerald-200">
            <p className="font-medium">Signed in successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-email" className="text-sm font-medium text-slate-300">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className="w-full rounded-lg border border-slate-600 bg-slate-900/60 px-3 py-2.5 text-white placeholder-slate-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-password" className="text-sm font-medium text-slate-300">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                placeholder="••••••••"
                className="w-full rounded-lg border border-slate-600 bg-slate-900/60 px-3 py-2.5 text-white placeholder-slate-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              {isLoading ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Sign up'}
            </button>

            {status === 'error' && errorMsg && (
              <p
                role="alert"
                className="rounded-lg border border-red-700/60 bg-red-900/30 px-3 py-2 text-sm text-red-200"
              >
                {errorMsg}
              </p>
            )}

            <p className="text-center text-sm text-slate-400">
              {mode === 'signin' ? (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signup');
                      setStatus('idle');
                      setErrorMsg(null);
                    }}
                    className="font-medium text-primary-400 hover:text-primary-300"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setStatus('idle');
                      setErrorMsg(null);
                    }}
                    className="font-medium text-primary-400 hover:text-primary-300"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
